import { rejectCodes } from './constants';

export const FarmRiskType = {
  Fo1: 'FO1',
  Fo2: 'FO2',
  Fo3: 'FO3',
  Fo4: 'FO4',
  Fo6Scheduled: 'FO6Scheduled',
  Fo6Structures: 'FO6Structures',
  Fo6Unscheduled: 'FO6Unscheduled',
  InlandMarine: 'InlandMarine'
};

// The classic FO risk types every farm brand supports; standalone types (InlandMarine) are
// per-brand opt-ins — see Brand.getAllowedFarmRiskTypes.
export const baseFarmRiskTypes: string[] = [
  FarmRiskType.Fo1,
  FarmRiskType.Fo2,
  FarmRiskType.Fo3,
  FarmRiskType.Fo4,
  FarmRiskType.Fo6Scheduled,
  FarmRiskType.Fo6Structures,
  FarmRiskType.Fo6Unscheduled
];

export const FarmScheduleCoverageType = {
  SPP: 'SPP',
  RELATED_PRIVATE_STRUCTURES: 'RELATED_PRIVATE_STRUCTURES',
  MINE_SUBSIDENCE_COVERAGE: 'MINE_SUBSIDENCE_COVERAGE',
  ANIMALS_4H_FFA: 'ANIMALS_4H_FFA',
  PEAK: 'PEAK',
  INCOME: 'INCOME'
};

export const EntityType = {
  Individual: 'Individual',
  Business: 'Business',
  Joint: 'Joint',
  Estate: 'Estate',
  Partnership: 'Partnership',
  Corporation: 'Corporation',
  LimitedLiabilityCompany: 'LimitedLiabilityCompany',
  Trust: 'Trust',
  Association: 'Association',
  NotForProfitOrganization: 'NotForProfitOrganization',
  SubchapterSCorporation: 'SubchapterSCorporation'
};

export const ScheduledFarmItemTypes = {
  COMBINES: '1',
  CUSTOM_FARM_MACHINERY: '2',
  DESCRIBED_MACHINERY: '3',
  GRAIN: '4',
  HAY: '5',
  IRRIGATION_EQUIPMENT: '6',
  LIVESTOCK: '7',
  MACHINERY_NOT_DESCRIBED: '8',
  OTHER_FARM_SUPPLIES: '9',
  PERSONAL_PROPERTY: '10',
  POULTRY_EXCLUDING_TURKEYS: '11',
  RENTED_OR_BORROWED_EQUIPMENT: '12',
  TURKEYS: '13'
};

export const FarmScheduleCoverageTypesByState = {
  IL: [
    FarmScheduleCoverageType.SPP,
    FarmScheduleCoverageType.MINE_SUBSIDENCE_COVERAGE,
    FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES,
    FarmScheduleCoverageType.ANIMALS_4H_FFA,
    FarmScheduleCoverageType.PEAK,
    FarmScheduleCoverageType.INCOME
  ],
  MO: [FarmScheduleCoverageType.SPP, FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES]
};

export const StructureCategory = {
  BarnsOutbuildings: 'BO',
  SilosGrainBins: 'SG',
  DwellingContents: 'DC',
  PoultryBuildings: 'PO',
  ConfinementBuildings: 'C',
  HoopBuildings: 'H',
  SolarPanels: 'SP',
  Fencing: 'F',
  PrivatePowerLightPoles: 'PL',
  TrailerContents: 'TC',
  WindmillsWindchargers: 'WW',
  WindmillsSteel: 'WS',
  RadioTv: 'RT'
};

/**
 * FO6 Coverage E structure categories that have no roof — Hoop Buildings and Silos/Grain Bins
 * (Farm only), plus Fencing and Solar Panels (Farm, FarmHome, FarmRental; Solar is CFM/MO-only).
 * For these, Roof Loss Settlement and the roof exclusion/restriction don't apply, so they are
 * hidden in staff-fe and suppressed on the dec/output. Shared single source of truth consumed by
 * staff-fe FO6.jsx and docs-engine farm-data-mapper. (#33841 CFM / #33849 Forreston)
 */
export const ROOFLESS_STRUCTURE_CATEGORIES: string[] = [
  StructureCategory.HoopBuildings,
  StructureCategory.SilosGrainBins,
  StructureCategory.SolarPanels,
  StructureCategory.Fencing
];

export const isRooflessStructureCategory = (buildingType?: string | null): boolean =>
  !!buildingType && ROOFLESS_STRUCTURE_CATEGORIES.includes(buildingType);

export const farmSoftRejectCodes: number[] = [
  rejectCodes.VERISK_NO360,
  rejectCodes.FARM_EARTHQUAKE_ZONE_APPROVAL_REQUIRED,
  rejectCodes.FARM_INLAND_MARINE_NO_ITEMS
];

export enum LiabilityType {
  Personal = 'P',
  Commercial = 'C'
}

export const InlandMarineTypes = {
  BoatDock: { id: 'boatDock', value: 'Boat Dock' },
  Boats: { id: 'boat', value: 'Watercraft/boats' },
  CoinCollection: { id: 'coinCollection', value: 'Coin Collection' },
  FineArts: { id: 'fineArts', value: 'Fine Arts' },
  Furs: { id: 'furs', value: 'Furs' },
  GolfCarts: { id: 'golfCart', value: 'Golf Carts' },
  GolfEquipment: { id: 'golfEquipment', value: 'Golf Equipment' },
  Guns: { id: 'guns', value: 'Guns' },
  Jewelry: { id: 'jewelry', value: 'Jewelry' },
  MiscellaneousProperty: { id: 'miscellaneousProperty', value: 'Miscellaneous Property' },
  MusicalInstruments: { id: 'musicalInstruments', value: 'Musical Instruments' },
  PersonalTools: { id: 'personalTools', value: 'Personal Tools' },
  Silverware: { id: 'silverware', value: 'Silverware' },
  SportingEquipment: { id: 'sportingEquipment', value: 'Sporting Equipment' },
  Tack: { id: 'tack', value: 'Tack' },
  UnmannedAircraft: { id: 'unmannedAircraft', value: 'Unmanned Aircraft Systems (Drones)' },
  Hardware: { id: 'hardware', value: 'Hardware' },
  Software: { id: 'software', value: 'Software' },
  ScheduledGlass: { id: 'scheduledGlass', value: 'Scheduled Glass' },
  LawnMowers: {
    id: 'lawnMowersGardenTractorsAndRelatedEquipment',
    value: 'Lawn Mowers, Garden Tractors, and Related Equipment'
  },
  Atv: { id: 'atv', value: 'ATVs and Related Equipment' },
  // #34019: Gator/Utility Transport Vehicle — a CFM Inland Marine type that mirrors ATV
  // (rates the same, spp_type_item = 1, and feeds the incidental-liability assets section).
  GatorUtilityTransportVehicle: { id: 'gatorUtilityTransportVehicle', value: 'Gator/Utility Transport Vehicle' }
};
